<template>
  <Menubar
    class="doNotPrintMe h-100"
    style="border-radius: 0px"
    :class="{
      prod: !environment.isDevelopment,
      dev: environment.isDevelopment,
    }"
  >
    <template #start>
      <div class="flex">
        <img class="logo hideOnMobile" :src="lisaImage" @click="goToLisaButtonTarget" />
        <div
          v-if="isSpecialAdmin()"
          class="ml-2 flex flex align-items-center gap-2"
          style="z-index: 9001; position: fixed; left: 200px"
        >
          <LISACheckbox v-if="isLISA3" v-model="isLISA3" class="m-0 p-0"> LISA 3 </LISACheckbox>
          <Checkbox v-if="!isLISA3" id="lisa3Override" v-model="isLISA3" binary></Checkbox>
          <label v-if="!isLISA3" for="lisa3Override"> LISA 3 </label>
        </div>
        <div id="breadcrumbs" class="ellipsis breadcrumbs breadcrumb hideOnMobile" />
        <div class="showOnMobile">
          <LISAButton
            class="p-2"
            size="small"
            outlined
            style="color: var(--p-primary-contrast-color) !important"
            @click="$emit('update:showSidebar', !showSidebar)"
          >
            <i class="fas fa-bars" />
          </LISAButton>
        </div>
      </div>
    </template>
    <template #end>
      <div class="flex align-items-center">
        <div id="boardTime" class="mr-3 hideOnMobile"></div>

        <div class="flex align-items-center gap-2">
          <MeetItem :board-id="currentBoardId" />
          <LISAButton
            v-if="showNewGlobalSearch"
            v-tooltip.bottom="t('global.search')"
            type="button"
            icon="fas fa-magnifying-glass"
            rounded
            severity="secondary"
            :aria-label="t('global.search')"
            @click="$emit('openGlobalSearch')"
          ></LISAButton>
          <LISAButton
            icon="fas fa-question"
            type="button"
            rounded
            severity="secondary"
            @click="toggleHelpMenu"
          >
            <template v-if="isModifierSet(ModifierType.sunAndMoon) && data != null" #icon>
              <InfoTooltip>
                <template #default>
                  <div class="pt-2 p-4 rounded-2xl shadow bg-white text-center w-64">
                    <div class="text-4xl mb-2">{{ data?.icon }}</div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.nightEnd") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.nightEnd) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.nauticalDawn") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.nauticalDawn) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.dawn") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.dawn) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.sunrise") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.sunrise) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.sunriseEnd") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.sunriseEnd) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.goldenHourEnd") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.goldenHourEnd) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.solarNoon") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.solarNoon) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.goldenHour") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.goldenHour) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.sunsetStart") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.sunsetStart) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.sunset") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.sunset) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.dusk") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.dusk) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.nauticalDusk") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.nauticalDusk) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.night") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.night) }}</span>
                    </div>
                    <div class="text-sm">
                      {{ t("components.HeadNavbar.sunAndMoon.nadir") }}:
                      <span class="font-bold">{{ formatTime(data?.times?.nadir) }}</span>
                    </div>
                  </div>
                </template>
                <template #activator>
                  {{ data?.icon }}
                </template>
              </InfoTooltip>
            </template>
          </LISAButton>
          <PrimeMenu
            ref="helpMenu"
            class="HeadNavBarScoped"
            :model="helpItems"
            :popup="true"
            style="font-size: 0.8em; width: 20em"
          />
          <Avatar
            :picture-id="pictureId"
            :avatar-size="37.71"
            data-testId="userMenuAvatar"
            :name="preferredName"
            class="hoverClick"
            @click="toggleUserMenu"
          />
          <PrimeMenu
            ref="userMenu"
            :model="userItems"
            class="HeadNavBarScoped"
            :popup="true"
            style="font-size: 0.8em; width: 20em; max-height: calc(100vh - 4rem); overflow-y: auto"
          >
            <template #item="{ item, props: itemProps }">
              <a v-bind="itemProps.action" class="flex align-items-center">
                <span v-if="item.icon" v-bind="itemProps.icon" />
                <span v-bind="itemProps.label">{{ item.label }}</span>
                <span
                  v-if="item.activeLanguage"
                  class="pi pi-check ml-auto text-primary"
                  aria-hidden="true"
                />
              </a>
            </template>
          </PrimeMenu>
        </div>
      </div>
    </template>
  </Menubar>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Menubar from "primevue/menubar";
import PrimeMenu from "primevue/menu";
import { useTranslation } from "@/language/translation";
import { ref, computed } from "vue";
import type { MenuItem } from "primevue/menuitem";
import Avatar from "@/components/AvatarImage.vue";
import lisalogo from "@/assets/logos/lisa.png";
import emmalogo from "@/assets/logos/emma.png";
import MeetItem from "@/modules/meet/MeetItem.vue";
import { languageNames, flagMap } from "../language";
import isKey from "@/functions/isKey";
import { ENV } from "@/ENV";
import LISAButton from "./shared/primevue/LISAButton.vue";
import { isDevelopment, isSpecialAdmin } from "../functions/environment";
import { useSunAndMoon } from "@/functions/sunAndMoon";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";
import InfoTooltip from "./shared/InfoTooltip.vue";
import vTooltip from "primevue/tooltip";
import { resolveLisaButtonRoute } from "@/functions/lisaButtonNavigation";
import { LISACheckbox } from "@fi-solutions/lisa-ui";
import Checkbox from "primevue/checkbox";
import { isLISA3 } from "@/composables/useLISA3.ts";
defineProps({
  showSidebar: {
    type: Boolean,
    required: true,
  },
});
defineEmits({
  "update:showSidebar": (show: boolean) => show,
  openGlobalSearch: null,
});

function formatTime(date?: Date): string {
  return date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--:--";
}

const helpMenu = ref<InstanceType<typeof PrimeMenu>>();
const userMenu = ref<InstanceType<typeof PrimeMenu>>();
const toggleHelpMenu = (event: any) => {
  helpMenu.value?.toggle(event);
};
const toggleUserMenu = (event: any) => {
  userMenu.value?.toggle(event);
};

const { t, locale } = useTranslation();
const router = useRouter();
const store = useStore();
const showNewGlobalSearch = computed(() => isModifierSet(ModifierType.LISA3));

const environment = computed(() => {
  return {
    isDevelopment: isDevelopment(),
  };
});

const currentBoardId = computed(() => {
  return parseInt(router.currentRoute.value.params?.boardId as string, 10);
});
const lisaButtonTarget = computed(() => {
  return store.getters["login/lisaButtonTarget"];
});
const startUpBoardId = computed(() => {
  return store.state.login.user?.startUpBoardId as number | null | undefined;
});

const preferredName = computed(() => {
  return store.state.login.user?.preferredName;
});
const pictureId = computed(() => {
  return store.getters["login/profilePictureId"] as string | undefined;
});

const site = computed(() => {
  return store.state.login.user?.site?.name ?? "";
});
const department = computed(() => {
  return store.state.login.user?.department?.name ?? "";
});
function isDepartmentThere() {
  if (department.value) {
    return true;
  }
  return false;
}
function createLinkAndLeave(link = "") {
  if (link === "") {
    return;
  }
  const clickableLink = document.createElement("a");
  clickableLink.href = link;
  clickableLink.target = "_blank";
  clickableLink.click();
}
function openDocumentation() {
  if (document.location.hostname === "localhost") {
    createLinkAndLeave("https://wiki.service.lisapp.de/login/intern");
  } else {
    createLinkAndLeave(`https://wiki.service.lisapp.de/login/${ENV.VITE_APP_INSTANCE_KEY}`);
  }
}
function openImprint() {
  createLinkAndLeave(`https://frisch-solutions.com/${locale.value}/impressum/`);
}
function openPrivacyStatement() {
  createLinkAndLeave(`https://frisch-solutions.com/${locale.value}/datenschutz/`);
}
function goToLisaButtonTarget() {
  const target = resolveLisaButtonRoute(lisaButtonTarget.value, startUpBoardId.value);
  if (router.currentRoute.value.fullPath !== target) {
    router.push(target);
  }
}
async function changeLanguage(language: string) {
  store.dispatch("language/setLocale", language);
}
async function logout() {
  await store.dispatch("login/logout");
}
function sendEmail(address: any) {
  window.open(`mailto:${address}`, "_blank");
}
const helpItems = computed<MenuItem[]>(() => [
  {
    label: "Help-Desk",

    items: [
      {
        separator: true,
      },
      {
        label: "+49 241 51537043",
        icon: "pi pi-phone",
        command: () => {
          window.open("tel:+4924151537043", "_blank");
        },
      },
      {
        label: "support@frisch-solutions.com",
        icon: "pi pi-envelope",
        command: () => {
          sendEmail("support@frisch-solutions.com");
        },
        style: "p-menuitem-link",
      },
      {
        separator: true,
      },
      {
        label: "Changelog",
        command: () => {
          router.push("/changelog");
        },
      },
      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.documentation"),
        command: () => {
          openDocumentation();
        },
      },
      {
        label: t("components.HeadNavbar.imprint"),
        command: () => {
          openImprint();
        },
      },
      {
        label: t("components.HeadNavbar.privacyStatement"),
        command: () => {
          openPrivacyStatement();
        },
      },
    ],
  },
]);

const userItems = computed<MenuItem[]>(() => [
  {
    label: preferredName.value,
    items: [
      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.UserSettings"),
        icon: "fas fa-user-cog",
        class: "UserSettings",
        command: () =>
          router.push({
            name: "UpdateUser",
            params: { id: store.state.login.user.id.toString() },
          }),
      },
      {
        label: site.value,
        icon: "fas fa-industry",
      },
      {
        label: department.value,
        icon: "pi pi-phone",
        visible: isDepartmentThere(),
      },
      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.Language"),
        icon: "fas fa-language",
        disabled: true,
      },
      ...Object.entries(languageNames).map(([key, label]) => {
        return {
          label,
          icon: isKey(flagMap, key) ? flagMap[key] : undefined,
          activeLanguage: key.replace("_", "-") === locale.value,
          command: () => changeLanguage(key),
        };
      }),

      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.logout"),
        icon: "fas fa-sign-out",
        command: () => logout(),
      },
    ],
  },
]);

const { data } = useSunAndMoon(computed(() => isModifierSet(ModifierType.sunAndMoon)));

const lisaImage = ENV.VITE_APP_BRAND_NAME === "EMMA" ? emmalogo : lisalogo;
</script>

<style media="screen" lang="scss" scoped>
.dev {
  background: var(--p-error-color) !important;
  border-color: var(--p-error-color) !important;
}

.staging {
  background: var(--p-info-color) !important;
  border-color: var(--p-info-color) !important;
}

.prod {
  background: var(--p-primary-color) !important;
  border-color: var(--p-primary-color) !important;
}

.logo {
  height: 2.4em;
  cursor: pointer;
  width: 9.47em;
  background-color: var(--p-surface-0);
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  padding: 0.125rem;
}
</style>

<style lang="scss">
.noUnderline > button {
  text-decoration: none !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.help-div {
  height: 36px;
  width: 36px;
}

.HeadNavBarScoped {
  .p-submenu-header {
    font-size: 1.5em !important;
  }
}
</style>
<style media="screen" scoped>
.breadcrumbs {
  position: absolute;

  left: 50%;
  transform: translate(-50%);
}
</style>

<style scoped>
.ellipsis {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  padding: 0.53rem 1rem;
  margin-bottom: 1rem;
  list-style: none;
  background-color: var(--p-gray-200);
  border-radius: var(--p-border-radius-md);
}
.hoverClick:hover {
  cursor: pointer;
  filter: brightness(50%);
  transition: all 0.1s ease-out;
}
</style>
